import { setHttpError } from "client/Features/ErrorPage/errorStore";
import { getParsedValue, useStableImperativeCallback } from "client/Hooks";
import { isNil, isNotNil } from "client/Misc/utils";
import { never } from "client_legacy/ndaw/errors";
import {
	ReactElement,
	useMemo,
	useCallback,
	FunctionComponent
} from "react";
import {
	NavigateFunction,
	Params,
	To,
	useLocation,
	useNavigate,
	useParams,
	useSearchParams
} from "react-router";
import {
	Navigation,
	NavigationCallback,
	NavigationOptions
} from "./navigation";

export function connectToRouter<
	TBaseProps extends {},
	TPropNames extends StringKeyOf<TBaseProps>
>(
	BaseComponent: FunctionComponent<TBaseProps>,
	configurations: {
		[key in TPropNames]: PropOption<
			undefined extends TBaseProps[key] ? false : true,
			TBaseProps[key]
		>
	}
): FunctionComponent<Omit<TBaseProps, TPropNames>> {
	const ConnectedToRouter = (props: Omit<TBaseProps, TPropNames>): ReactElement | null => {
		const params: Readonly<Params<string>> = useParams();
		const [searchParams] = useSearchParams();
		const { state: historyState } = useLocation();
		const onNavigate: NavigateFunction = useNavigate();
		const stableOnNavigate: NavigateFunction = useStableImperativeCallback(onNavigate) as NavigateFunction;

		const navigationHandler = useCallback(
			(navigation: Navigation | string, options?: NavigationOptions): void => {
				stableOnNavigate(
					convertNavigationToRouterNavigation(
						navigation
					),
					options && {
						replace: options.shouldReplace,
						state: options.historyState
					}
				);

			},
			[stableOnNavigate]
		);

		const resolvedPropResult = useMemo(
			(): ResolvePropsResult<TBaseProps, TPropNames> => tryResolvePropsFromParameters(
				params,
				searchParams,
				historyState,
				navigationHandler,
				configurations
			),
			[
				params,
				searchParams,
				historyState,
				navigationHandler
			]
		);

		if (!resolvedPropResult.wasSuccessful) {
			setHttpError({ statusCode: 404 });
			return null;
		}

		const resultProps = {
			...props,
			...resolvedPropResult.result
		} as TBaseProps;

		return (
			<BaseComponent
				{...resultProps}
			/>
		);
	};
	ConnectedToRouter.displayName = "ConnectedToRouter";

	return ConnectedToRouter;
}

type QueryParameterConfigurationIgnoreInvalidPart = {
	type: "query";
	shouldIgnoreInvalidValues?: boolean;
};

export type ParameterConfiguration<TIsRequired extends boolean, TValue> = {
	isRequired: TIsRequired;
	customParameterName?: string;
}
	& (
		TIsRequired extends false
			? (
				{
					type?: "route";
				}
				| QueryParameterConfigurationIgnoreInvalidPart
			)
			:
			{
				type?: "route" | "query";
			}
	)
	& (
		[TValue] extends [string | undefined]
			? {
				tryParse?: (rawValue: string | undefined) => TryResult<TValue | undefined>;
			}
			: {
				tryParse: (rawValue: string | undefined) => TryResult<TValue | undefined>;
			}
	);

export enum PredefinedPropOptionKind {
	HistoryState = 0,
	NavigateCallback = 1
}

type PredefinedPropOptions<TIsRequired extends boolean, TValue> = unknown extends TValue
	? TIsRequired extends false
		? PredefinedPropOptionKind.HistoryState
		: never
	: TValue extends NavigationCallback
		? PredefinedPropOptionKind.NavigateCallback
		: never;

export type PropOption<TIsRequired extends boolean, TValue> =
	| PredefinedPropOptions<TIsRequired, TValue>
	| ParameterConfiguration<TIsRequired, TValue>;

type ResolvePropsResult<TBaseProps extends {}, TPropNames extends StringKeyOf<TBaseProps>> = TryResult<
	{ [key in TPropNames]: TBaseProps[key]; }
>;

function tryResolvePropsFromParameters<
	TBaseProps extends {},
	TPropNames extends StringKeyOf<TBaseProps>
>(
	parameters: Record<string, string | undefined>,
	searchParameters: URLSearchParams,
	historyState: unknown,
	navigateHandler: NavigationCallback,
	configurations: {
		[key in TPropNames]: PropOption<
			undefined extends TBaseProps[key] ? false : true,
			TBaseProps[key]
		>;
	}
): ResolvePropsResult<TBaseProps, TPropNames> {
	const result: Partial<{ [key in TPropNames]: TBaseProps[key]; }> = {};

	for (const propName in configurations) {
		if (!Object.prototype.hasOwnProperty.call(configurations, propName)) {
			continue;
		}

		const element = configurations[propName] as (
			| ParameterConfiguration<boolean, TBaseProps[typeof propName]>
			| PredefinedPropOptionKind.HistoryState
			| PredefinedPropOptionKind.NavigateCallback
		);

		if (element === PredefinedPropOptionKind.HistoryState) {
			result[propName] = historyState as TBaseProps[Extract<TPropNames, string>] | undefined;
			continue;
		}

		if (element === PredefinedPropOptionKind.NavigateCallback) {
			result[propName] = navigateHandler as TBaseProps[Extract<TPropNames, string>];
			continue;
		}

		const {
			isRequired,
			type = "route",
			tryParse,
			customParameterName: parameterName = propName
		} = element;

		let shouldIgnoreInvalidValues: boolean = false;

		if (!element.isRequired && element.type === "query") {
			shouldIgnoreInvalidValues = (element as QueryParameterConfigurationIgnoreInvalidPart).shouldIgnoreInvalidValues === true;
		}

		const parameter: string | nil = type === "query"
			? searchParameters.get(parameterName)
			: type === "route"
				? parameters[parameterName]
				: never(type);

		let parsedValue = getParsedValue(
			parameter,
			tryParse ?? outputRawValue
		) as OptionalModel<TBaseProps[typeof propName] | undefined>;

		if (parsedValue.isSet
			&& isNil(parsedValue.value)
			&& shouldIgnoreInvalidValues
		) {
			parsedValue = {
				isSet: false
			};
		}

		const isValid: boolean = isRequired
			? parsedValue.isSet && isNotNil(parsedValue.value)
			: !parsedValue.isSet || isNotNil(parsedValue.value);

		if (!isValid) {
			return {
				wasSuccessful: false
			};
		}

		result[propName] = parsedValue.isSet
			? parsedValue.value
			: undefined;
	}

	return {
		wasSuccessful: true,
		result: result as { [key in TPropNames]: TBaseProps[key]; }
	};
}

function outputRawValue(rawValue: string | undefined): TryResult<string | undefined> {
	return { wasSuccessful: true, result: rawValue };
}

export function resolveNavigationOptions(
	navigation: Navigation | string
): Navigation {
	if (typeof navigation === "string") {
		return {
			path: navigation,
			queryString: undefined
		};
	}

	return navigation;
}

export function convertNavigationToRouterNavigation(
	navigation: Navigation | string,
): To {
	const {
		path,
		queryString,
		fragment
	} = resolveNavigationOptions(navigation);

	return {
		pathname: path,
		search: queryString,
		hash: fragment
	};
}